import {
  ARA_EDIT_CHAT_FRAME_TYPES,
  ARA_GENERATION_FRAME_TYPES,
  ARA_INTAKE_FRAME_TYPES,
  ARA_META_SECTION_KEY,
  type AraEditMetaPatch,
  type AraIncomingEditMetaPatch,
  type AraStreamEvent
} from '@/features/ara-generation/types/ara-events'

const KNOWN_FRAME_TYPES: ReadonlySet<string> = new Set([
  ...ARA_GENERATION_FRAME_TYPES,
  ...ARA_EDIT_CHAT_FRAME_TYPES,
  ...ARA_INTAKE_FRAME_TYPES
])

const DATA_PREFIX = 'data:'

/**
 * Thrown when an ARA SSE stream ends without a terminal `done` or `error`
 * frame — i.e. the connection dropped mid-stream and what the consumer
 * received is incomplete. Consumers map this onto the same failure path an
 * I/O error already hits (never a clean completion). Suppressed when the
 * caller's `AbortSignal` has fired (user-initiated Stop is not a failure).
 * See ADR 012.
 */
export class AraStreamTruncatedError extends Error {
  constructor() {
    super('ARA stream ended without a terminal done/error frame (truncated)')
    this.name = 'AraStreamTruncatedError'
  }
}

const META_SECTION_INNER_RE =
  /<section\b[^>]*\bdata-section-key=["']meta["'][^>]*>([\s\S]*?)<\/section>/i

function extractMetaJsonPayload(html: string): string {
  const wrapped = html.match(META_SECTION_INNER_RE)
  const candidate = (wrapped?.[1] ?? html).trim()
  const firstBrace = candidate.indexOf('{')
  const lastBrace = candidate.lastIndexOf('}')
  if (firstBrace === -1 || lastBrace <= firstBrace) return candidate
  return candidate.slice(firstBrace, lastBrace + 1)
}

/**
 * Strip `sector` from a meta patch.
 *
 * Sector is not a label: it selects the peer cohort every failure probability,
 * benchmark and comparable-customer figure was computed against. A meta patch
 * only rewrites the hero header, so accepting one would relabel the page while
 * leaving all of those numbers on the OLD industry — a report that looks
 * corrected and is not (SCP-112). Changing it requires regeneration.
 *
 * Enforced here as well as in the prompt because a prompt is a request, not a
 * constraint, and this is the last point before the patch reaches the document.
 * Returns `null` when sector was the ONLY field, so a sector-only patch becomes
 * no edit at all rather than an empty one.
 */
function stripSectorFromMetaPatch(
  patch: AraIncomingEditMetaPatch
): AraEditMetaPatch | null {
  const { sector: _sector, ...rest } = patch
  return Object.keys(rest).length > 0 ? rest : null
}

function normalizeMetaEditFrame(frame: Record<string, unknown>): AraStreamEvent | null {
  if (frame.type !== 'edit' || frame.section_key !== ARA_META_SECTION_KEY) {
    return frame as AraStreamEvent
  }

  if (frame.patch && typeof frame.patch === 'object' && !Array.isArray(frame.patch)) {
    const patch = stripSectorFromMetaPatch(frame.patch as AraIncomingEditMetaPatch)
    if (!patch) return null
    const { html: _html, ...rest } = frame
    return { ...rest, patch } as AraStreamEvent
  }

  if (typeof frame.html !== 'string') return null

  try {
    const parsed = JSON.parse(extractMetaJsonPayload(frame.html)) as unknown
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    const patch = stripSectorFromMetaPatch(parsed as AraIncomingEditMetaPatch)
    if (!patch) return null
    return {
      type: 'edit',
      section_key: ARA_META_SECTION_KEY,
      patch
    }
  } catch {
    return null
  }
}

/**
 * Parse one raw SSE line into a typed frame, or `null` when the line should be
 * silently skipped (no `data:` prefix, malformed JSON, non-object payload,
 * missing/non-string `type`, or an unknown frame type). Never throws — additive
 * backend changes and malformed input must not break the stream consumer.
 *
 * Mirrors the `apps/agent-ui` `agent-service.ts` parse loop: strip the
 * `data: ` prefix → `JSON.parse` → skip malformed. Adds a known-type allowlist
 * so unknown future frames are dropped instead of yielded as untyped junk.
 *
 * Meta edits from master's wire format (`edit` + `section_key: "meta"` + JSON
 * in `html`, often wrapped by `section_html`) are normalized to `{ patch }`.
 */
function parseLine(line: string): AraStreamEvent | null {
  const trimmed = line.trimStart()
  if (!trimmed.startsWith(DATA_PREFIX)) return null

  const raw = trimmed.slice(DATA_PREFIX.length).trim()
  if (!raw) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null

  const type = (parsed as { type?: unknown }).type
  if (typeof type !== 'string' || !KNOWN_FRAME_TYPES.has(type)) return null

  return normalizeMetaEditFrame(parsed as Record<string, unknown>)
}

/**
 * Drive a fault-tolerant ARA SSE parser over an async iterable of raw string
 * chunks (as decoded from the HTTP response body). Splits on `\n`, keeps the
 * trailing partial line buffered across reads, strips `data: `, JSON-parses,
 * and yields only valid typed frames in order — silently skipping malformed
 * lines and unknown frame types.
 *
 * Fault-tolerant about frame *content*, strict about stream *termination*
 * (ADR 012): when the iterable finishes without a terminal `done` or `error`
 * frame having been yielded, throws {@link AraStreamTruncatedError}. The throw
 * is suppressed when `signal` is aborted — a user-initiated Stop is not a
 * failure.
 */
export async function* parseAraStreamFromChunks(
  chunks: AsyncIterable<string> | Iterable<string>,
  signal?: AbortSignal
): AsyncGenerator<AraStreamEvent> {
  let buffer = ''
  let sawTerminal = false
  for await (const chunk of chunks) {
    buffer += chunk
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      const frame = parseLine(line)
      if (frame) {
        if (frame.type === 'done' || frame.type === 'error') sawTerminal = true
        yield frame
      }
    }
  }

  const tail = parseLine(buffer)
  if (tail) {
    if (tail.type === 'done' || tail.type === 'error') sawTerminal = true
    yield tail
  }

  if (!sawTerminal && !signal?.aborted) throw new AraStreamTruncatedError()
}

/**
 * Drive the ARA SSE parser over a fetch `ReadableStream<Uint8Array>` (the
 * `response.body` of a `text/event-stream` response). Decodes bytes with a
 * streaming `TextDecoder` and delegates to {@link parseAraStreamFromChunks};
 * `signal` is forwarded so a user-initiated abort suppresses the truncation
 * throw at EOF.
 */
export async function* parseAraStream(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal
): AsyncGenerator<AraStreamEvent> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  try {
    yield* parseAraStreamFromChunks(
      {
        async *[Symbol.asyncIterator]() {
          while (true) {
            const { done, value } = await reader.read()
            if (done) return
            yield decoder.decode(value, { stream: true })
          }
        }
      },
      signal
    )
  } finally {
    reader.releaseLock()
  }
}
